/**
 * V38.07 — Dashboard analytics LEGALCOOP (Super Admin).
 *
 * Affiche : recettes totales / 30j / 12 mois, MRR proxy, churn,
 * breakdown par dispositif, refunds, série mensuelle.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import {
  BarChart3, TrendingUp, TrendingDown, RefreshCw, Loader2,
  Wallet, AlertTriangle, DollarSign, Users, ChartBar,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => {
  const t = localStorage.getItem('token')
        || localStorage.getItem('access_token')
        || localStorage.getItem('admin_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtEUR = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);

export default function LegalcoopAnalyticsPanel() {
  const [overview, setOverview] = useState(null);
  const [churn, setChurn] = useState(null);
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [o, c, r] = await Promise.all([
        axios.get(`${API}/admin/legalcoop/analytics/overview`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/legalcoop/analytics/churn`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/legalcoop/analytics/refunds`, { headers: getAuthHeader() }),
      ]);
      setOverview(o.data);
      setChurn(c.data);
      setRefunds(r.data?.refunds || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur chargement analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="legalcoop-analytics-loading">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    );
  }

  const totals = overview?.totals || {};
  const services = overview?.by_service || [];
  const monthly = overview?.monthly_revenue_12m || [];
  const maxMonthly = Math.max(...monthly.map((m) => m.revenue || 0), 1);

  return (
    <Card data-testid="legalcoop-analytics-panel">
      <CardHeader className="border-b">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-blue-900">
              <BarChart3 className="h-5 w-5" />
              LEGALCOOP — Analytics financiers
            </CardTitle>
            <CardDescription>
              Recettes, MRR proxy, churn et refunds des services LEGALCOOP (Stripe).
            </CardDescription>
          </div>
          <Button onClick={fetchAll} variant="outline" size="sm" data-testid="legalcoop-analytics-refresh">
            <RefreshCw className="h-4 w-4 mr-1" /> Actualiser
          </Button>
        </div>
      </CardHeader>

      <CardContent className="pt-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="overview" data-testid="legalcoop-analytics-tab-overview">
              Vue d'ensemble
            </TabsTrigger>
            <TabsTrigger value="services" data-testid="legalcoop-analytics-tab-services">
              Par dispositif
            </TabsTrigger>
            <TabsTrigger value="monthly" data-testid="legalcoop-analytics-tab-monthly">
              Série mensuelle
            </TabsTrigger>
            <TabsTrigger value="refunds" data-testid="legalcoop-analytics-tab-refunds">
              Refunds
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="pt-3" data-testid="legalcoop-analytics-overview">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card>
                <CardContent className="pt-4">
                  <p className="text-xs text-slate-500 flex items-center gap-1"><DollarSign className="h-3 w-3" /> Recettes totales</p>
                  <p className="text-2xl font-bold text-blue-800" data-testid="kpi-all-time-revenue">{fmtEUR(totals.all_time_revenue)}</p>
                  <p className="text-[10px] text-slate-400">{totals.paid_orders || 0} commandes payées</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <p className="text-xs text-slate-500 flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Recettes 30 jours</p>
                  <p className="text-2xl font-bold text-emerald-700" data-testid="kpi-revenue-30d">{fmtEUR(totals.revenue_30d)}</p>
                  <p className="text-[10px] text-slate-400">MRR proxy</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <p className="text-xs text-slate-500 flex items-center gap-1"><ChartBar className="h-3 w-3" /> Recettes 12 mois</p>
                  <p className="text-2xl font-bold text-indigo-700" data-testid="kpi-revenue-12m">{fmtEUR(totals.revenue_12m)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <p className="text-xs text-slate-500 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Refunds</p>
                  <p className="text-2xl font-bold text-red-700" data-testid="kpi-refunded-amount">{fmtEUR(totals.refunded_amount)}</p>
                  <p className="text-[10px] text-slate-400">{totals.refunded_orders || 0} remboursements</p>
                </CardContent>
              </Card>
            </div>

            {churn && (
              <Card className="mt-4">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-6 flex-wrap">
                    <div>
                      <p className="text-xs text-slate-500 flex items-center gap-1"><Users className="h-3 w-3" /> Clients total</p>
                      <p className="text-2xl font-bold text-slate-800" data-testid="kpi-customers-total">{churn.total_customers || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 flex items-center gap-1"><Wallet className="h-3 w-3" /> Actifs 60j</p>
                      <p className="text-2xl font-bold text-emerald-700" data-testid="kpi-customers-active">{churn.active_60d || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 flex items-center gap-1"><TrendingDown className="h-3 w-3" /> Churn proxy</p>
                      <p className="text-2xl font-bold text-amber-700" data-testid="kpi-churn-rate">{churn.churn_rate_pct}%</p>
                      <p className="text-[10px] text-slate-400">{churn.churned_proxy || 0} clients inactifs &gt; 60j</p>
                    </div>
                  </div>
                  {churn.note && <p className="text-[11px] text-slate-500 mt-2 italic">{churn.note}</p>}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="services" className="pt-3">
            <div className="border rounded">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs">
                  <tr>
                    <th className="px-3 py-2">Dispositif</th>
                    <th className="px-3 py-2 text-right">Recettes</th>
                    <th className="px-3 py-2 text-right">Commandes</th>
                    <th className="px-3 py-2 text-right">Refunds</th>
                  </tr>
                </thead>
                <tbody>
                  {services.length === 0 ? (
                    <tr><td colSpan={4} className="px-3 py-6 text-center italic text-slate-500">Aucune donnée.</td></tr>
                  ) : services.map((s) => (
                    <tr key={s.service_type} className="border-t" data-testid={`legalcoop-service-row-${s.service_type}`}>
                      <td className="px-3 py-2"><Badge variant="outline">{s.service_type}</Badge></td>
                      <td className="px-3 py-2 text-right font-semibold text-blue-800">{fmtEUR(s.revenue)}</td>
                      <td className="px-3 py-2 text-right">{s.count}</td>
                      <td className="px-3 py-2 text-right text-red-700">{s.refunded > 0 ? fmtEUR(s.refunded) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="monthly" className="pt-3" data-testid="legalcoop-analytics-monthly">
            <p className="text-sm text-slate-600 mb-3">Recettes des 12 derniers mois (barres horizontales)</p>
            <div className="space-y-1">
              {monthly.map((m) => (
                <div key={m.month} className="flex items-center gap-2 text-xs">
                  <span className="w-16 font-mono text-slate-600">{m.month}</span>
                  <div className="flex-1 bg-slate-100 rounded h-5 relative overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-indigo-600 h-full transition-all"
                      style={{ width: `${(m.revenue / maxMonthly) * 100}%` }}
                    />
                  </div>
                  <span className="w-24 text-right font-semibold text-blue-800">{fmtEUR(m.revenue)}</span>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="refunds" className="pt-3">
            {refunds.length === 0 ? (
              <p className="italic text-slate-500">Aucun refund / chargeback enregistré.</p>
            ) : (
              <div className="border rounded">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs">
                    <tr>
                      <th className="px-3 py-2">Service</th>
                      <th className="px-3 py-2">Email</th>
                      <th className="px-3 py-2 text-right">Montant</th>
                      <th className="px-3 py-2">Statut</th>
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Raison</th>
                    </tr>
                  </thead>
                  <tbody>
                    {refunds.map((r) => (
                      <tr key={r.id} className="border-t" data-testid={`legalcoop-refund-row-${r.id}`}>
                        <td className="px-3 py-2"><Badge variant="outline">{r.service_type}</Badge></td>
                        <td className="px-3 py-2 font-mono text-[11px]">{r.client_email || '—'}</td>
                        <td className="px-3 py-2 text-right text-red-700">{fmtEUR(r.amount_ttc)}</td>
                        <td className="px-3 py-2"><Badge variant="outline" className="text-red-700 border-red-300">{r.status}</Badge></td>
                        <td className="px-3 py-2 text-xs">{r.updated_at ? new Date(r.updated_at).toLocaleDateString('fr-FR') : '—'}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">{r.refund_reason || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
